import { providerLabel } from "@/lib/account";
import type { ParamSpec, ProviderInfoWire } from "@/api/types";

/** Market data providers users can select for backtests and live strategies. */
export const ENABLED_MARKET_PROVIDERS = ["binance", "mexc", "revolutx"] as const;

export type EnabledMarketProvider = (typeof ENABLED_MARKET_PROVIDERS)[number];

export type MarketTradeType = "spot" | "futures";

/** A symbol of one exchange market. The same name can exist on both spot and futures. */
export type MarketListing = { symbol: string; market: MarketTradeType };

/**
 * Spot or futures, from the market config's `market_type` (absent = the
 * backend's SPOT default). Never inferred from the symbol name.
 */
export function marketTypeOf(config?: Record<string, unknown> | null): MarketTradeType {
  const mt = String(config?.market_type ?? "").toUpperCase();
  return mt === "FUTURES" || mt === "SWAP" || mt === "PERP" ? "futures" : "spot";
}

/**
 * One config per market a provider offers (the options of its `market_type`
 * parameter), each built on `base`. A provider without the parameter has one market.
 */
export function marketVariants(
  base: Record<string, unknown>,
  specs: ParamSpec[]
): { market: MarketTradeType; config: Record<string, unknown> }[] {
  const options = specs.find((s) => s.name === "market_type")?.options ?? [];
  if (!options.length) return [{ market: marketTypeOf(base), config: base }];
  return options.map((market_type) => {
    const config = { ...base, market_type };
    return { market: marketTypeOf(config), config };
  });
}

export function normalizeMarketProvider(provider?: string | null): string {
  return String(provider || "").trim().toLowerCase();
}

export function isMarketProviderEnabled(provider?: string | null): provider is EnabledMarketProvider {
  const p = normalizeMarketProvider(provider);
  if (!p) return false;
  return (ENABLED_MARKET_PROVIDERS as readonly string[]).includes(p);
}

/** Canonical enabled provider id, or null if unavailable. */
export function enabledMarketProvider(provider?: string | null): EnabledMarketProvider | null {
  const p = normalizeMarketProvider(provider);
  return isMarketProviderEnabled(p) ? (p as EnabledMarketProvider) : null;
}

/** Enabled providers first (stable ENABLED order), then the rest alphabetically. */
export function sortMarketProviders(providers: string[]): string[] {
  const enabledRank = new Map<string, number>(
    ENABLED_MARKET_PROVIDERS.map((id, i) => [id, i])
  );
  return [...providers].sort((a, b) => {
    const aKey = normalizeMarketProvider(a);
    const bKey = normalizeMarketProvider(b);
    const aEn = enabledRank.has(aKey);
    const bEn = enabledRank.has(bKey);
    if (aEn !== bEn) return aEn ? -1 : 1;
    if (aEn && bEn) return (enabledRank.get(aKey) ?? 0) - (enabledRank.get(bKey) ?? 0);
    return aKey.localeCompare(bKey);
  });
}

/**
 * Spot/futures support from `configurable.providers` tags.
 * If neither tag is present (legacy/incomplete metadata), both stay allowed.
 */
export function marketProviderTradeTypes(
  info?: ProviderInfoWire | null
): { spot: boolean; futures: boolean } {
  const tags = info?.tags ?? [];
  const spot = tags.includes("spot");
  const futures = tags.includes("futures");
  if (!spot && !futures) return { spot: true, futures: true };
  return { spot, futures };
}

export function marketProviderLabel(provider: string): string {
  return providerLabel(provider);
}
